import React from 'react';
import { Link } from 'react-router-dom';
import { FaCheckCircle, FaUsers, FaBuilding, FaRocket, FaCrown, FaShieldAlt, FaChartLine } from 'react-icons/fa';
import SEOHead from '../components/SEOHead';

const EmployerLandingPage = () => {
  return (
    <div>
      <SEOHead
        title="Hire AV Technicians & Stagehands | StageCrew for Employers"
        description="Post gigs, review profile- and preference-based matches, and manage candidates from your StageCrew employer dashboard. First 10 posts are free."
        url="https://stagecrew.app/for-employers"
        keywords="hire stagehands, hire AV technicians, event staffing platform, stagehand staffing, AV crew hiring, live event crew, freelance stagehands, production staffing"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Service',
          name: 'StageCrew Employer Platform',
          url: 'https://stagecrew.app/for-employers',
          description: 'Post live event gigs, review profile- and preference-based matches, and manage candidate outreach from an employer dashboard.',
          serviceType: 'Event Staffing Platform',
          areaServed: {
            '@type': 'Country',
            name: 'United States',
          },
          provider: {
            '@type': 'Organization',
            name: 'StageCrew',
            url: 'https://stagecrew.app',
            logo: 'https://stagecrew.app/icon-512.png',
          },
          offers: {
            '@type': 'AggregateOffer',
            lowPrice: '99',
            highPrice: '499',
            priceCurrency: 'USD',
            offerCount: '3',
          },
        }}
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-indigo-700 via-blue-600 to-blue-700 text-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block bg-white bg-opacity-20 text-sm font-bold px-4 py-1.5 rounded-full mb-6">
                First 10 Gig Posts Free
              </div>
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Find AV Crews &<br />Stagehands Clearly
              </h1>
              <p className="text-xl md:text-2xl mb-8 text-blue-100 max-w-xl">
                Post your gig, review matches based on worker profiles and preferences, and manage every candidate from one dashboard.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/register?role=employer"
                  className="bg-white text-blue-700 px-10 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition shadow-lg text-center"
                >
                  Post Your First Gig Free
                </Link>
                <a
                  href="#pricing"
                  className="bg-transparent border-2 border-white text-white px-10 py-4 rounded-lg font-bold text-lg hover:bg-white hover:bg-opacity-10 transition text-center"
                >
                  See Pricing
                </a>
              </div>
              <p className="mt-6 text-blue-200 text-sm">No credit card required for your first 10 posts.</p>
            </div>

            {/* Mock Dashboard Card */}
            <div className="hidden lg:block">
              <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-200 text-gray-900">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-lg">Your Gigs</h3>
                  <span className="bg-indigo-100 text-indigo-700 text-xs font-bold px-3 py-1 rounded-full">Dashboard Preview</span>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-indigo-50 rounded-lg">
                    <div>
                      <p className="font-semibold text-sm">Concert Load-In — Las Vegas</p>
                      <p className="text-xs text-gray-500">Source: profile match</p>
                    </div>
                    <span className="bg-indigo-100 text-indigo-800 text-xs font-bold px-2 py-0.5 rounded-full">Reviewing</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                    <div>
                      <p className="font-semibold text-sm">Corporate AV — Orlando</p>
                      <p className="text-xs text-gray-500">Preferences aligned</p>
                    </div>
                    <span className="bg-blue-100 text-blue-800 text-xs font-bold px-2 py-0.5 rounded-full">Contact</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                    <div>
                      <p className="font-semibold text-sm">Film Lighting — Atlanta</p>
                      <p className="text-xs text-gray-500">Application received</p>
                    </div>
                    <span className="bg-yellow-100 text-yellow-800 text-xs font-bold px-2 py-0.5 rounded-full">Status Visible</span>
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-sm">
                  <span className="text-gray-500">See match source and status</span>
                  <span className="text-indigo-600 font-semibold">Contact & review</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Facts */}
      <section className="bg-white border-b border-gray-100 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-2xl font-bold text-blue-600">10 Free Posts</div>
              <div className="text-gray-600 text-sm mt-1">For new employer accounts</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-600">Profile Matching</div>
              <div className="text-gray-600 text-sm mt-1">Uses worker details and preferences</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-600">Clear Statuses</div>
              <div className="text-gray-600 text-sm mt-1">See candidate source and progress</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-600">Direct Workflow</div>
              <div className="text-gray-600 text-sm mt-1">Contact workers and leave reviews</div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works for Employers */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-4">Start Your Hiring Workflow in 3 Steps</h2>
          <p className="text-center text-gray-600 mb-16 text-lg max-w-2xl mx-auto">
            Keep gig details, candidate context, and follow-up in one employer workflow.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="bg-indigo-100 text-indigo-600 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <FaBuilding className="text-3xl" />
              </div>
              <div className="bg-indigo-600 text-white text-sm font-bold px-3 py-1 rounded-full inline-block mb-3">
                Step 1
              </div>
              <h3 className="text-xl font-bold mb-3">Post Your Gig</h3>
              <p className="text-gray-600">
                Specify roles, dates, location, rate, and requirements so workers can evaluate the opportunity.
              </p>
            </div>
            <div className="text-center">
              <div className="bg-indigo-100 text-indigo-600 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <FaUsers className="text-3xl" />
              </div>
              <div className="bg-indigo-600 text-white text-sm font-bold px-3 py-1 rounded-full inline-block mb-3">
                Step 2
              </div>
              <h3 className="text-xl font-bold mb-3">Review Relevant Profiles</h3>
              <p className="text-gray-600">
                StageCrew uses worker profile details and job preferences to surface relevant candidates for your review.
              </p>
            </div>
            <div className="text-center">
              <div className="bg-indigo-100 text-indigo-600 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <FaCheckCircle className="text-3xl" />
              </div>
              <div className="bg-indigo-600 text-white text-sm font-bold px-3 py-1 rounded-full inline-block mb-3">
                Step 3
              </div>
              <h3 className="text-xl font-bold mb-3">Track, Contact & Review</h3>
              <p className="text-gray-600">
                Use the dashboard to see each candidate's source and status, contact workers directly, and leave post-gig reviews.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why StageCrew vs Agencies */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">A Hiring Workflow You Can Inspect</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-sm">
              <FaChartLine className="text-3xl text-indigo-600 mb-4" />
              <h3 className="text-xl font-bold mb-2">Stay in Control</h3>
              <p className="text-gray-600">
                Manage your gigs and candidate workflow from the employer dashboard.
              </p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-sm">
              <FaShieldAlt className="text-3xl text-indigo-600 mb-4" />
              <h3 className="text-xl font-bold mb-2">Review the Details</h3>
              <p className="text-gray-600">
                Inspect worker-provided profile details, preferences, and available review history before deciding.
              </p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-sm">
              <FaUsers className="text-3xl text-indigo-600 mb-4" />
              <h3 className="text-xl font-bold mb-2">Follow Up Directly</h3>
              <p className="text-gray-600">
                Contact candidates through the available workflow and add a review after the gig.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Product Trust */}
      <section className="py-16 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-4">Trust the Details You Can See</h2>
          <p className="text-center text-gray-600 mb-12 text-lg max-w-2xl mx-auto">
            StageCrew gives employers concrete context for each candidate instead of relying on inflated marketplace claims.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center p-6">
              <h3 className="text-xl font-bold text-indigo-600 mb-2">Profiles</h3>
              <div className="text-gray-600 text-sm">Review the details workers choose to provide.</div>
            </div>
            <div className="text-center p-6">
              <h3 className="text-xl font-bold text-indigo-600 mb-2">Preferences</h3>
              <div className="text-gray-600 text-sm">Matching considers the work each person wants.</div>
            </div>
            <div className="text-center p-6">
              <h3 className="text-xl font-bold text-indigo-600 mb-2">Source & Status</h3>
              <div className="text-gray-600 text-sm">See where candidates came from and where they stand.</div>
            </div>
            <div className="text-center p-6">
              <h3 className="text-xl font-bold text-indigo-600 mb-2">Reviews</h3>
              <div className="text-gray-600 text-sm">Build useful history through post-gig feedback.</div>
            </div>
          </div>
        </div>
      </section>

      {/* Roles You Can Hire */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-4">Hire Any Role on Set</h2>
          <p className="text-center text-gray-600 mb-10 text-lg">
            Create gigs for common live-event roles, from load-in through strike.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              'Audio Technician', 'Lighting Tech', 'Gaffer', 'Key Grip', 'Grip',
              'Stagehand', 'Rigger', 'Camera Operator', 'LED Tech', 'AV Technician',
              'Stage Manager', 'Production Assistant', 'Projectionist', 'Sound Engineer',
            ].map((role) => (
              <span
                key={role}
                className="bg-indigo-50 text-indigo-700 border border-indigo-200 px-4 py-2 rounded-full text-sm font-medium"
              >
                {role}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Verifiable Employer Workflow */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-4">Built Around Verifiable Details</h2>
          <p className="text-center text-gray-600 mb-12 text-lg max-w-2xl mx-auto">
            Make hiring decisions from the information and activity visible in your account.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Profiles You Can Inspect</h3>
              <p className="text-gray-600">
                Review worker-provided role, skill, certification, and preference details before making contact.
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Progress You Can Follow</h3>
              <p className="text-gray-600">
                Candidate source and status stay visible in the employer dashboard as you review your options.
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Relationships You Can Build</h3>
              <p className="text-gray-600">
                Contact workers directly through the available workflow and leave reviews after completed gigs.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-4">Simple Employer Pricing</h2>
          <p className="text-center text-gray-600 mb-4 text-lg">
            Start free. Scale when you need to.
          </p>
          <p className="text-center text-green-600 font-semibold mb-12">
            Every new employer gets 10 free gig posts — no credit card required.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Starter */}
            <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200">
              <FaBuilding className="text-2xl text-gray-500 mb-3" />
              <h3 className="text-2xl font-bold mb-2">Starter</h3>
              <div className="flex items-baseline mb-6">
                <span className="text-5xl font-bold">$99</span>
                <span className="text-gray-500 ml-2">/month</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center">
                  <FaCheckCircle className="text-green-500 mr-3 flex-shrink-0" />
                  <span>5 job postings per month</span>
                </li>
                <li className="flex items-center">
                  <FaCheckCircle className="text-green-500 mr-3 flex-shrink-0" />
                  <span>Profile and preference matching</span>
                </li>
                <li className="flex items-center">
                  <FaCheckCircle className="text-green-500 mr-3 flex-shrink-0" />
                  <span>Employer dashboard</span>
                </li>
                <li className="flex items-center">
                  <FaCheckCircle className="text-green-500 mr-3 flex-shrink-0" />
                  <span>Candidate source and status visibility</span>
                </li>
              </ul>
              <Link
                to="/register?role=employer"
                className="block w-full text-center bg-gray-100 text-gray-800 px-6 py-3 rounded-lg font-bold hover:bg-gray-200 transition"
              >
                Get Started
              </Link>
            </div>

            {/* Growth */}
            <div className="bg-white rounded-xl shadow-lg p-8 border-2 border-blue-600 relative">
              <FaRocket className="text-2xl text-blue-600 mb-3" />
              <h3 className="text-2xl font-bold mb-2">Growth</h3>
              <div className="flex items-baseline mb-6">
                <span className="text-5xl font-bold">$249</span>
                <span className="text-gray-500 ml-2">/month</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center">
                  <FaCheckCircle className="text-green-500 mr-3 flex-shrink-0" />
                  <span className="font-medium">20 job postings per month</span>
                </li>
                <li className="flex items-center">
                  <FaCheckCircle className="text-green-500 mr-3 flex-shrink-0" />
                  <span>Profile and preference matching</span>
                </li>
                <li className="flex items-center">
                  <FaCheckCircle className="text-green-500 mr-3 flex-shrink-0" />
                  <span>Employer dashboard</span>
                </li>
                <li className="flex items-center">
                  <FaCheckCircle className="text-green-500 mr-3 flex-shrink-0" />
                  <span>Candidate source and status visibility</span>
                </li>
                <li className="flex items-center">
                  <FaCheckCircle className="text-green-500 mr-3 flex-shrink-0" />
                  <span>Direct contact and review workflow</span>
                </li>
              </ul>
              <Link
                to="/register?role=employer"
                className="block w-full text-center bg-blue-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-blue-700 transition"
              >
                Get Started
              </Link>
            </div>

            {/* Enterprise */}
            <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200">
              <FaCrown className="text-2xl text-purple-600 mb-3" />
              <h3 className="text-2xl font-bold mb-2">Enterprise</h3>
              <div className="flex items-baseline mb-6">
                <span className="text-5xl font-bold">$499</span>
                <span className="text-gray-500 ml-2">/month</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center">
                  <FaCheckCircle className="text-green-500 mr-3 flex-shrink-0" />
                  <span className="font-medium">Unlimited job postings</span>
                </li>
                <li className="flex items-center">
                  <FaCheckCircle className="text-green-500 mr-3 flex-shrink-0" />
                  <span>Profile and preference matching</span>
                </li>
                <li className="flex items-center">
                  <FaCheckCircle className="text-green-500 mr-3 flex-shrink-0" />
                  <span>Employer dashboard</span>
                </li>
                <li className="flex items-center">
                  <FaCheckCircle className="text-green-500 mr-3 flex-shrink-0" />
                  <span>Candidate source and status visibility</span>
                </li>
                <li className="flex items-center">
                  <FaCheckCircle className="text-green-500 mr-3 flex-shrink-0" />
                  <span>Direct contact and review workflow</span>
                </li>
              </ul>
              <Link
                to="/register?role=employer"
                className="block w-full text-center bg-purple-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-purple-700 transition"
              >
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-4">Employer FAQ</h2>
          <p className="text-center text-gray-600 mb-12">
            <Link to="/for-employers/faq" className="text-indigo-600 hover:underline font-medium">View all employer FAQs →</Link>
          </p>
          <div className="space-y-6">
            {[
              {
                q: 'How do the 10 free gig posts work?',
                a: 'Every new employer account gets 10 free gig posts — no credit card required. Post your jobs, review profile- and preference-based matches, and see the employer workflow before choosing a plan.',
              },
              {
                q: 'What determines worker matches?',
                a: 'StageCrew uses worker profile details and job preferences to surface relevant candidates. Results depend on the active profiles and preferences that align with your gig, so StageCrew does not promise a specific match time or fill.',
              },
              {
                q: 'Where can I post a gig?',
                a: 'Add the actual work location to each gig. Worker availability varies by location and preferences, and your dashboard shows the candidates connected to that specific post.',
              },
              {
                q: 'What worker details can I review?',
                a: 'Workers provide profile details such as roles, experience, certifications, preferences, and references. Review those details, contact the worker directly, and verify any qualification required for your gig.',
              },
              {
                q: 'What do the listed plans cost?',
                a: 'The employer page lists Starter at $99 per month, Growth at $249 per month, and Enterprise at $499 per month. New employer accounts receive 10 free gig posts before choosing a paid plan.',
              },
            ].map(({ q, a }) => (
              <div key={q} className="border-b border-gray-200 pb-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2">{q}</h3>
                <p className="text-gray-600">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Talk to Us CTA */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Have Questions? Talk to a Human.</h2>
          <p className="text-gray-600 text-lg mb-8">
            Not ready to sign up yet? No problem. Email Edwin directly — he'll walk you through how StageCrew fits your crew needs.
          </p>
          <a
            href="mailto:edwin@stagecrew.app?subject=StageCrew%20Employer%20Inquiry"
            className="inline-block bg-indigo-600 text-white px-10 py-4 rounded-lg font-bold text-lg hover:bg-indigo-700 transition shadow-lg"
          >
            Email Us: edwin@stagecrew.app
          </a>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-gradient-to-r from-indigo-700 to-blue-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to Fill Your Next Gig?</h2>
          <p className="text-xl mb-8 text-blue-100 max-w-2xl mx-auto">
            Post your first gig free, review profile-based matches, and track candidate source and status in your dashboard.
          </p>
          <Link
            to="/register?role=employer"
            className="bg-white text-blue-700 px-12 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition shadow-lg inline-block"
          >
            Post Your First Gig Free
          </Link>
          <p className="mt-4 text-blue-200 text-sm">
            Already have an account? <Link to="/login" className="underline hover:text-white">Log in</Link>
          </p>
        </div>
      </section>
    </div>
  );
};

export default EmployerLandingPage;
